"use client";

import React from "react";
import { Card, CardContent, Spinner } from "@heroui/react";

import { logger } from "@/telemetry";
import { normalizeText, extractSalientTokens } from "@/utils/textMatching";

export interface CSVViewerProps {
  /** CSV/TSV document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Citation text used to find and highlight matching rows */
  citationText?: string;

  /** Filename to determine delimiter (CSV vs TSV) */
  filename?: string;
}

/**
 * Find rows that match the citation text using token overlap scoring.
 * Normalises the citation and each row, then counts the proportion of
 * salient tokens present in the concatenated row values.
 * @returns Indices of rows with score >= 0.3 (at least 30% token overlap)
 */
function findMatchingRows(rows: string[][], citation: string): Set<number> {
  const normalised = normalizeText(citation);
  const tokens = extractSalientTokens(normalised);

  if (tokens.length === 0) {
    return new Set();
  }

  const matched = new Set<number>();

  for (let i = 0; i < rows.length; i++) {
    const rowText = normalizeText(rows[i].join(" "));
    const hits = tokens.filter((token) => rowText.includes(token)).length;
    const score = hits / tokens.length;

    if (score >= 0.3) {
      matched.add(i);
    }
  }

  return matched;
}

/**
 * CSV/TSV Viewer component
 * Displays tabular data in a scrollable table with row-matching highlight behaviour.
 * When citation text is provided, rows with sufficient token overlap are highlighted
 * and the first match is auto-scrolled into view.
 */
export const CSVViewer: React.FC<CSVViewerProps> = ({ documentData, citationText, filename }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tableData, setTableData] = React.useState<string[][]>([]);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const firstMatchRef = React.useRef<HTMLTableRowElement>(null);

  // Parse CSV/TSV data
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    let isMounted = true;

    const parseCSV = async () => {
      try {
        setLoading(true);
        setError(null);

        // Decode ArrayBuffer as UTF-8 text
        const decoder = new TextDecoder("utf-8");
        const text = decoder.decode(documentData);

        // Determine delimiter (comma for CSV, tab for TSV)
        const isTSV = filename?.toLowerCase().endsWith(".tsv");
        const delimiter = isTSV ? "\t" : ",";

        // Parse CSV/TSV into rows
        const rows = parseCSVText(text, delimiter);

        if (!isMounted) return;

        if (rows.length === 0) {
          setError("Empty file");
          setLoading(false);

          return;
        }

        // First row as headers
        setHeaders(rows[0]);

        // Remaining rows as data (limit to first 1000 rows for performance)
        const dataRows = rows.slice(1, 1001);

        setTableData(dataRows);
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to parse CSV/TSV", err);
        if (isMounted) {
          setError("Failed to parse CSV/TSV file");
          setLoading(false);
        }
      }
    };

    parseCSV();

    return () => {
      isMounted = false;
    };
  }, [documentData, filename]);

  /** Memoised set of row indices that match the citation text */
  const matchingRows = React.useMemo(
    () =>
      citationText && tableData.length > 0
        ? findMatchingRows(tableData, citationText)
        : new Set<number>(),
    [tableData, citationText],
  );

  // Auto-scroll to the first highlighted row once loading completes
  React.useEffect(() => {
    if (!loading && matchingRows.size > 0 && firstMatchRef.current?.scrollIntoView) {
      firstMatchRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, matchingRows]);

  /**
   * Simple CSV parser
   * Handles basic CSV with quoted fields
   */
  const parseCSVText = (text: string, delimiter: string): string[][] => {
    const rows: string[][] = [];
    const lines = text.split(/\r?\n/);

    for (const line of lines) {
      if (line.trim().length === 0) continue;

      const row: string[] = [];
      let currentField = "";
      let insideQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          // Toggle quote state
          if (insideQuotes && line[i + 1] === '"') {
            // Escaped quote
            currentField += '"';
            i++; // Skip next quote
          } else {
            insideQuotes = !insideQuotes;
          }
        } else if (char === delimiter && !insideQuotes) {
          // Field separator
          row.push(currentField.trim());
          currentField = "";
        } else {
          currentField += char;
        }
      }

      // Add last field
      row.push(currentField.trim());
      rows.push(row);
    }

    return rows;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner label="Loading CSV/TSV..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-danger-500">{error}</p>
      </div>
    );
  }

  // Track whether we have assigned the firstMatchRef to the first matching row
  let firstMatchIndex = -1;

  if (matchingRows.size > 0) {
    for (const idx of matchingRows) {
      if (firstMatchIndex === -1 || idx < firstMatchIndex) firstMatchIndex = idx;
    }
  }

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      <div className="max-w-full space-y-4">
        {/* Table Display */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-default-100 sticky top-0 z-10">
                  <tr>
                    {/* Row number column */}
                    <th className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200">
                      #
                    </th>
                    {headers.map((header, idx) => (
                      <th
                        key={idx}
                        className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200 whitespace-nowrap"
                      >
                        {header || `Column ${idx + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIdx) => {
                    const isMatch = matchingRows.has(rowIdx);
                    const isFirstMatch = rowIdx === firstMatchIndex;

                    return (
                      <tr
                        key={rowIdx}
                        ref={isFirstMatch ? firstMatchRef : undefined}
                        className={
                          isMatch
                            ? "border-b border-default-200 bg-warning-100"
                            : "border-b border-default-200 hover:bg-default-50"
                        }
                        data-highlight={isMatch ? "true" : undefined}
                      >
                        {/* Row number */}
                        <td className="px-3 py-2 text-xs text-default-500 border-r border-default-200 bg-default-50">
                          {rowIdx + 1}
                        </td>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="px-3 py-2 text-xs text-default-700 border-r border-default-200 whitespace-nowrap"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Show info if table is truncated */}
        {tableData.length === 1000 && (
          <div className="text-xs text-default-500 text-center">
            Showing first 1,000 rows (file may contain more)
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVViewer;
